"use client";

import Link from "next/link";
import clsx from "clsx";
import { useMemo } from "react";
import { PlayIcon } from "lucide-react";

import type { BlogArticle } from "@/types";
import { getPreviewMedia, slugify } from "@/lib/utils";

import GalleryRasterImage from "@/components/media/GalleryRasterImage";
import { GLASS_PLAY_BUTTON_CLASS, GLASS_PLAY_ICON_CLASS } from "@/components/ui/glass-play-button";

type Props = {
	blog: BlogArticle;
};

export default function BlogCard({ blog }: Props) {
	const { _id, title, slug, media, thumbnail, blogCategories, readingTime, isPremium } = blog;

	const categories = useMemo(() => blogCategories.map((x) => x.name), [blogCategories]);
	const thumbMedia = useMemo(
		() => getPreviewMedia(media, thumbnail, blog.thumbnailDisplayUrl),
		[media, thumbnail, blog.thumbnailDisplayUrl],
	);
	const fallbackRasterSources = useMemo(
		() =>
			media
				.filter((item) => item.type !== "VIDEO" && item.url?.trim())
				.map((item) => item.url!.trim())
				.filter((url) => url !== thumbMedia?.url?.trim()),
		[media, thumbMedia?.url],
	);

	// Use database slug if available, fallback to ID for backward compatibility
	const href = slug ? `/blogs/${slug}` : `/blogs/${_id}/${slugify(title)}`;

	const isVideo = thumbMedia?.type === "VIDEO";

	return (
		<Link
			href={href}
			className="relative grid min-h-20 grid-cols-[8.5rem_1fr] gap-4"
		>
			{thumbMedia !== null ? (
				<div className="relative h-auto max-h-20 w-full self-center overflow-hidden rounded-lg">
					<GalleryRasterImage
						source={thumbMedia}
						fallbackRasterSources={fallbackRasterSources}
						alt={title}
						loading="lazy"
						className="h-auto max-h-20 w-full rounded-lg object-cover"
					/>
					{isVideo && (
						<div className="absolute inset-0 flex items-center justify-center bg-black/20">
							<span className={clsx(GLASS_PLAY_BUTTON_CLASS, "h-5 w-8")}>
								<PlayIcon className={clsx(GLASS_PLAY_ICON_CLASS, "size-3")} aria-hidden />
							</span>
						</div>
					)}
				</div>
			) : (
				<div className="size-20 rounded-lg bg-grey-foreground">
					<span className="text-sm">No media</span>
				</div>
			)}

			<div className="flex flex-col text-sm text-grey-foreground">
				<span className="text-xsm2 font-medium leading-tight text-foreground">{title}</span>
				<span className="text-xxsm">{categories.join(" | ")}</span>

				<div className="flex-1" />

				<div className="flex items-center justify-between">
					<span className="text-xxsm">{readingTime}</span>
					<div
						className={clsx(
							"flex h-5 w-16 items-center justify-center rounded-full text-xxsm font-medium uppercase",
							blog.isPremium ? "bg-black text-tertiary" : "bg-tertiary text-white",
						)}
					>
						{blog.isPremium ? "Premium" : "Free"}
					</div>
				</div>
			</div>
		</Link>
	);
}
